import React, {useEffect, useState} from 'react';
import TableActionButtons from "app/main/shared-components/base-table/TableActionButtons";
import {Column, TableActionsTypes} from "app/types/TableTypes";
import PageHeader from "app/main/shared-components/page-header/PageHeader";
import BaseTable from "app/main/shared-components/base-table/BaseTable";
import history from '@history';
import SearchInput from "../../shared-components/search-input/SearchInput";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "app/store";
import {fetchAllSeatClasses} from "../store/seat-classes/seat-classes.slice";
import {useRouteMatch} from "react-router-dom";
import {toggleQuickPanel} from "../../shared-components/quick-panel/store";
import {Switch} from "@material-ui/core";
import {SeatClassService} from "app/services";
import {SeatClassStatus} from "app/types/service-types/AircraftTypes";
import PaginationBar from "../../shared-components/pagination-bar/PaginationBar";
import {warningMsg, successMsg, errorMsg} from "app/utils/MessageUtils";

const SeatClassesPage = () => {
  const dispatch = useDispatch();
  let {url} = useRouteMatch();
  const {
    seatClasses,
    loading,
    pagination
  } = useSelector((state: AppState) => state.configurations.seatClasses.seatClasses);

  const [filteredClasses, setFilteredClasses] = useState(seatClasses || []);
  const [currentPage, setCurrentPage] = useState(0);

  const handleStatusToggle = async (classId: string, currentStatus: SeatClassStatus) => {
    const newStatus: SeatClassStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      const response = await SeatClassService.updateStatus(classId, newStatus);
      
      // Show warning if there are affected seat definitions
      if (response.affectedSeatDefinitions && response.affectedSeatDefinitions > 0) {
        dispatch(warningMsg(
          `This seat class is used by ${response.affectedSeatDefinitions} seat(s) across your aircraft. They will continue to work normally.`
        ));
      } else {
        dispatch(successMsg(`Seat class status updated to ${newStatus}`));
      }
      
      dispatch(fetchAllSeatClasses({ page: currentPage }));
    } catch (err) {
      dispatch(errorMsg(err, 'Error updating seat class status'));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    dispatch(fetchAllSeatClasses({ page }));
  };

  const tableActions: TableActionsTypes = {
    onEditRow: (rowId: number) => {
      dispatch(toggleQuickPanel());
      history.push(`${url}/update/${rowId}`);
    }
  }

  const columns: Column[] = [
    {
      Header: 'Name',
      accessor: 'name'
    },
    {
      Header: 'Base Price',
      accessor: 'basePrice',
      Cell: ({cell}: any) => (
        <span>{cell.value != null ? `${cell.value.toFixed(2)}` : '-'}</span>
      )
    },
    {
      Header: 'Priority',
      accessor: 'priority'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({cell}: any) => (
        <Switch
          checked={cell.value === 'ACTIVE'}
          onChange={() => handleStatusToggle(cell.row.original.id, cell.value)}
          color="primary"
        />
      )
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: ({cell}: any) => (
        <TableActionButtons {...tableActions} rowId={cell.value} name={cell.row.original.name}/>
      )
    }
  ];

  const searchClasses = (value: string) => {
    if (value) {
      const filtered = seatClasses.filter((sc: any) => {
        return sc.name?.toLowerCase().match(value) ||
          sc.basePrice?.toString().match(value);
      });
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses(seatClasses);
    }
  }

  useEffect(() => {
    dispatch(fetchAllSeatClasses({ page: 0 }));
  }, []);

  useEffect(() => {
    setFilteredClasses(seatClasses);
  }, [seatClasses]);

  return (
    <div>
      <PageHeader
        title="Seat Classes"
        desc="Manage seat classes (Business, Economy Plus, Economy, etc.)"
        button="Register"
        onClickButton={() => {
          history.push(`${url}/create`);
          dispatch(toggleQuickPanel());
        }}
      />
      <div style={{marginTop: '10px', marginBottom: '10px'}}>
        <SearchInput onChange={searchClasses}/>
      </div>
      <BaseTable columns={columns} data={filteredClasses} isLoading={loading}/>
      {pagination.totalPages > 0 && (
        <PaginationBar 
          totalPages={pagination.totalPages} 
          currentPage={currentPage + 1}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default SeatClassesPage;
